import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { PageSpinner } from "@/components/ui/spinner";
import { cn, statusColor } from "@/lib/utils";
import { formatFieldTime, localDateKey } from "@/lib/datetime";
import { ChevronLeft, ChevronRight, CalendarDays, List, Calendar, Clock } from "lucide-react";

interface Job {
    id: string;
    title: string;
    status: string;
    customer_name: string | null;
    assigned_to: string | null;
    assigned_to_name: string | null;
    trade_name: string | null;
    trade_code: string | null;
    scheduled_start: string | null;
    scheduled_end: string | null;
    notes: string | null;
}

interface Trade {
    id: string;
    name: string;
    code: string;
}

interface TechUser {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
}

type ViewMode = "week" | "day";

const TRADE_COLORS: Record<string, string> = {
    backflow: "bg-blue-100 border-blue-300 text-blue-800",
    fire_ext: "bg-red-100 border-red-300 text-red-800",
    underground_fire: "bg-orange-100 border-orange-300 text-orange-800",
    plumbing: "bg-green-100 border-green-300 text-green-800",
    hvac: "bg-purple-100 border-purple-300 text-purple-800",
};

const TRADE_DOT: Record<string, string> = {
    backflow: "bg-blue-500",
    fire_ext: "bg-red-500",
    underground_fire: "bg-orange-500",
    plumbing: "bg-green-500",
    hvac: "bg-purple-500",
};

function startOfWeek(date: Date): Date {
    const d = new Date(date);
    const day = d.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    d.setDate(d.getDate() + diff);
    d.setHours(0, 0, 0, 0);
    return d;
}

function startOfDay(date: Date): Date {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
}

function addDays(date: Date, n: number): Date {
    const d = new Date(date);
    d.setDate(d.getDate() + n);
    return d;
}

function isSameDay(a: Date, b: Date): boolean {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function formatWeekRange(start: Date): string {
    const end = addDays(start, 6);
    const opts: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };
    if (start.getFullYear() !== end.getFullYear()) {
        return `${start.toLocaleDateString("en-US", { ...opts, year: "numeric" })} – ${end.toLocaleDateString("en-US", { ...opts, year: "numeric" })}`;
    }
    if (start.getMonth() !== end.getMonth()) {
        return `${start.toLocaleDateString("en-US", opts)} – ${end.toLocaleDateString("en-US", { ...opts, year: "numeric" })}`;
    }
    return `${start.toLocaleDateString("en-US", { month: "short" })} ${start.getDate()} – ${end.getDate()}, ${end.getFullYear()}`;
}

const HOURS = Array.from({ length: 14 }, (_, i) => i + 6); // 6 AM to 7 PM

export default function SchedulePage() {
    const navigate = useNavigate();
    const [viewMode, setViewMode] = useState<ViewMode>("week");
    const [anchor, setAnchor] = useState(() => startOfWeek(new Date()));
    const [tradeFilter, setTradeFilter] = useState("");
    const [techFilter, setTechFilter] = useState("");

    const weekStart = viewMode === "week" ? anchor : startOfWeek(anchor);
    const visibleStart = viewMode === "week" ? weekStart : startOfDay(anchor);
    const visibleEnd = viewMode === "week" ? addDays(weekStart, 7) : addDays(anchor, 1);

    const dateFrom = localDateKey(visibleStart);
    const dateTo = localDateKey(visibleEnd);

    const jobsQuery = useQuery({
        queryKey: ["schedule", "jobs", dateFrom, dateTo, tradeFilter, techFilter],
        queryFn: () => {
            const params = new URLSearchParams({
                date_from: dateFrom,
                date_to: dateTo,
                limit: "500",
            });
            if (tradeFilter) params.set("trade_id", tradeFilter);
            if (techFilter) params.set("assigned_to", techFilter);
            return api.get<{ jobs: Job[]; total: number }>(`/api/jobs?${params}`);
        },
    });

    const tradesQuery = useQuery({
        queryKey: ["trades"],
        queryFn: () => api.get<{ trades: Trade[] }>("/api/trades"),
        staleTime: 5 * 60_000,
    });

    const techsQuery = useQuery({
        queryKey: ["dispatch", "technicians"],
        queryFn: () => api.get<{ users: TechUser[] }>("/api/dispatch/technicians"),
        staleTime: 5 * 60_000,
    });

    const jobs = jobsQuery.data?.jobs ?? [];
    const trades = tradesQuery.data?.trades ?? [];
    const techs = techsQuery.data?.users ?? [];
    const today = new Date();

    const days = useMemo(
        () => (viewMode === "week" ? Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)) : [startOfDay(anchor)]),
        [viewMode, weekStart, anchor],
    );

    const jobsByDay = useMemo(() => {
        const map = new Map<string, Job[]>();
        for (const d of days) map.set(localDateKey(d), []);
        for (const job of jobs) {
            if (!job.scheduled_start) continue;
            const key = localDateKey(new Date(job.scheduled_start));
            const bucket = map.get(key);
            if (bucket) bucket.push(job);
        }
        for (const [, arr] of map) {
            arr.sort((a, b) => {
                const ta = a.scheduled_start ?? "";
                const tb = b.scheduled_start ?? "";
                return ta.localeCompare(tb);
            });
        }
        return map;
    }, [jobs, days]);

    function tradeColor(code: string): string {
        return TRADE_COLORS[code] ?? "bg-gray-100 border-gray-300 text-gray-700";
    }

    function tradeDot(code: string): string {
        return TRADE_DOT[code] ?? "bg-gray-400";
    }

    function navigatePrev() {
        if (viewMode === "week") setAnchor(addDays(anchor, -7));
        else setAnchor(addDays(anchor, -1));
    }

    function navigateNext() {
        if (viewMode === "week") setAnchor(addDays(anchor, 7));
        else setAnchor(addDays(anchor, 1));
    }

    function goToday() {
        if (viewMode === "week") setAnchor(startOfWeek(new Date()));
        else setAnchor(startOfDay(new Date()));
    }

    // #827: one path into day view, always for an explicit date — the week
    // columns' overflow rows land HERE so "open Friday" opens Friday, and
    // view switches never silently reset to today.
    function switchToDay(date: Date) {
        setViewMode("day");
        setAnchor(startOfDay(date));
    }

    function hourForJob(job: Job): number {
        if (!job.scheduled_start) return 8;
        return new Date(job.scheduled_start).getHours();
    }

    if (jobsQuery.isLoading && !jobsQuery.data) return <PageSpinner />;

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-black tracking-tight text-shell-foreground">Schedule</h1>
                    <p className="text-shell-foreground/80 text-sm">
                        {viewMode === "week" ? "Weekly" : "Daily"} calendar view of scheduled jobs.
                    </p>
                </div>
                <div className="flex gap-2">
                    {/* #827 M10a: this button LEAVES the calendar — say so.
                        "List View" read like a third calendar mode and dumped
                        new staff on the Jobs page with no way to know why. */}
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate("/jobs")}
                        title="Leaves the calendar and opens the Jobs page list"
                    >
                        <List className="h-4 w-4 mr-1" />
                        Open Jobs list →
                    </Button>
                </div>
            </div>

            {/* Controls bar */}
            <div className="flex flex-wrap items-center gap-3">
                {/* View toggle */}
                <div className="flex rounded-lg border overflow-hidden">
                    <button
                        onClick={() => {
                            setViewMode("week");
                            setAnchor(startOfWeek(anchor));
                        }}
                        className={cn(
                            "flex items-center gap-1.5 px-3 py-1.5 text-sm transition-colors",
                            viewMode === "week" ? "bg-primary text-primary-foreground" : "hover:bg-muted",
                        )}
                    >
                        <CalendarDays className="h-3.5 w-3.5" /> Week
                    </button>
                    <button
                        onClick={() => {
                            // #827 M10b: switching views keeps your place.
                            // From week view, land on today only when today is
                            // inside the visible week; otherwise the week's
                            // first day — never a silent jump back to today.
                            if (viewMode === "week") {
                                const now = new Date();
                                const inWeek = now >= weekStart && now < addDays(weekStart, 7);
                                switchToDay(inWeek ? now : weekStart);
                            } else {
                                switchToDay(anchor);
                            }
                        }}
                        className={cn(
                            "flex items-center gap-1.5 px-3 py-1.5 text-sm transition-colors",
                            viewMode === "day" ? "bg-primary text-primary-foreground" : "hover:bg-muted",
                        )}
                    >
                        <Calendar className="h-3.5 w-3.5" /> Day
                    </button>
                </div>

                {/* Navigation */}
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8"
                        aria-label={viewMode === "week" ? "Previous week" : "Previous day"}
                        onClick={navigatePrev}
                    >
                        <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm font-medium min-w-[180px] text-center">
                        {viewMode === "week"
                            ? formatWeekRange(weekStart)
                            : anchor.toLocaleDateString("en-US", {
                                  weekday: "long",
                                  month: "long",
                                  day: "numeric",
                                  year: "numeric",
                              })}
                    </span>
                    <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8"
                        aria-label={viewMode === "week" ? "Next week" : "Next day"}
                        onClick={navigateNext}
                    >
                        <ChevronRight className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={goToday}>
                        Today
                    </Button>
                </div>

                <div className="flex-1" />

                {/* Filters */}
                <Select value={tradeFilter} onChange={(e) => setTradeFilter(e.target.value)} className="w-40">
                    <option value="">All Trades</option>
                    {trades.map((t) => (
                        <option key={t.id} value={t.id}>
                            {t.name}
                        </option>
                    ))}
                </Select>

                <Select value={techFilter} onChange={(e) => setTechFilter(e.target.value)} className="w-44">
                    <option value="">All Technicians</option>
                    {techs.map((t) => (
                        <option key={t.id} value={t.id}>
                            {t.first_name} {t.last_name}
                        </option>
                    ))}
                </Select>
            </div>

            {/* Week view */}
            {viewMode === "week" && (
                <div className="grid grid-cols-7 gap-2">
                    {days.map((day) => {
                        const key = localDateKey(day);
                        const dayJobs = jobsByDay.get(key) ?? [];
                        const isToday = isSameDay(day, today);

                        return (
                            <div key={key} className="min-h-[200px]">
                                <div
                                    className={cn(
                                        "text-xs font-medium px-2 py-1.5 rounded-t-lg flex items-center justify-between",
                                        isToday ? "bg-primary text-primary-foreground" : "bg-muted text-foreground/80",
                                    )}
                                >
                                    <span>{day.toLocaleDateString("en-US", { weekday: "short" })}</span>
                                    {/* #827 C5: the column's TRUE total, visible
                                        before any scrolling or truncation. */}
                                    <span className="flex items-baseline gap-1">
                                        <span className="font-bold">{day.getDate()}</span>
                                        <span
                                            className={cn(
                                                "tabular-nums text-[11px]",
                                                isToday ? "opacity-90" : "opacity-70",
                                            )}
                                            title={`${dayJobs.length} job${dayJobs.length === 1 ? "" : "s"} scheduled this day`}
                                        >
                                            · {dayJobs.length}
                                        </span>
                                    </span>
                                </div>
                                <div className="border border-t-0 rounded-b-lg p-1 space-y-1 min-h-[170px]">
                                    {dayJobs.length === 0 && (
                                        <p className="text-xs text-foreground/80 text-center py-6">No jobs</p>
                                    )}
                                    {dayJobs.slice(0, 6).map((job) => (
                                        <div
                                            key={job.id}
                                            onClick={() => navigate(`/jobs/${job.id}`)}
                                            className={cn(
                                                "rounded-md border p-1.5 cursor-pointer hover:shadow-md transition-shadow text-xs",
                                                tradeColor(job.trade_code ?? ""),
                                            )}
                                        >
                                            <p className="font-medium truncate">{job.title}</p>
                                            <p className="truncate opacity-80">{job.customer_name}</p>
                                            <div className="flex items-center justify-between mt-0.5">
                                                <span className="flex items-center gap-1 opacity-70">
                                                    <Clock className="h-2.5 w-2.5" />
                                                    {job.scheduled_start ? formatFieldTime(job.scheduled_start) : "TBD"}
                                                </span>
                                                {job.assigned_to_name && (
                                                    <span className="truncate max-w-[60px] opacity-70">
                                                        {job.assigned_to_name.split(" ")[0]}
                                                    </span>
                                                )}
                                            </div>
                                            {job.trade_name && (
                                                <div className="flex items-center gap-1 mt-0.5">
                                                    <span
                                                        className={cn(
                                                            "h-1.5 w-1.5 rounded-full",
                                                            tradeDot(job.trade_code ?? ""),
                                                        )}
                                                    />
                                                    <span className="opacity-70 text-[10px]">{job.trade_name}</span>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                    {dayJobs.length > 6 && (
                                        // #827 C5: never a silent cap — the
                                        // overflow row is a real control that
                                        // opens THIS day's full view. The audit
                                        // walked a Friday showing 6 of 13 jobs
                                        // with no hint the rest existed.
                                        <button
                                            type="button"
                                            data-testid={`week-overflow-${key}`}
                                            onClick={() => switchToDay(day)}
                                            className="w-full rounded-md border border-dashed border-primary/40 px-1 py-1.5 text-[11px] font-semibold text-primary hover:bg-primary/10"
                                        >
                                            +{dayJobs.length - 6} more — open{" "}
                                            {day.toLocaleDateString("en-US", { weekday: "long" })}
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Day view — vertical timeline */}
            {viewMode === "day" && (
                <div className="border rounded-lg overflow-hidden">
                    {HOURS.map((hour) => {
                        const dayKey = localDateKey(anchor);
                        const hourJobs = (jobsByDay.get(dayKey) ?? []).filter((j) => hourForJob(j) === hour);
                        const label = `${hour === 0 ? 12 : hour > 12 ? hour - 12 : hour}:00 ${hour >= 12 ? "PM" : "AM"}`;

                        return (
                            <div key={hour} className="flex border-b last:border-b-0 min-h-[64px]">
                                <div className="w-20 shrink-0 p-2 text-xs text-foreground/80 font-medium border-r bg-muted/30 flex items-start pt-2">
                                    {label}
                                </div>
                                <div className="flex-1 p-2 flex flex-wrap gap-2">
                                    {hourJobs.map((job) => (
                                        <Card
                                            key={job.id}
                                            onClick={() => navigate(`/jobs/${job.id}`)}
                                            className={cn(
                                                "cursor-pointer hover:shadow-md transition-shadow border w-full sm:w-auto sm:min-w-[280px] sm:max-w-[400px]",
                                                tradeColor(job.trade_code ?? ""),
                                            )}
                                        >
                                            <CardContent className="p-3">
                                                <div className="flex items-center justify-between gap-2">
                                                    <p className="font-medium text-sm truncate">{job.title}</p>
                                                    <Badge className={statusColor(job.status)} variant="outline">
                                                        {job.status?.replace(/_/g, " ")}
                                                    </Badge>
                                                </div>
                                                <div className="flex items-center gap-3 mt-1.5 text-xs opacity-80">
                                                    <span>{job.customer_name}</span>
                                                    {job.assigned_to_name && <span>• {job.assigned_to_name}</span>}
                                                </div>
                                                <div className="flex items-center gap-3 mt-1 text-xs opacity-70">
                                                    <span className="flex items-center gap-1">
                                                        <Clock className="h-3 w-3" />
                                                        {job.scheduled_start
                                                            ? formatFieldTime(job.scheduled_start)
                                                            : "TBD"}
                                                        {job.scheduled_end
                                                            ? ` – ${formatFieldTime(job.scheduled_end)}`
                                                            : ""}
                                                    </span>
                                                    {job.trade_name && (
                                                        <span className="flex items-center gap-1">
                                                            <span
                                                                className={cn(
                                                                    "h-2 w-2 rounded-full",
                                                                    tradeDot(job.trade_code ?? ""),
                                                                )}
                                                            />
                                                            {job.trade_name}
                                                        </span>
                                                    )}
                                                </div>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
